import { serverTimestamp, addDoc, updateDoc, orderBy, setDoc, doc, getDoc, getDocs, query, where, deleteDoc, collection, or, documentId } from "firebase/firestore";
import { db, storage } from "../firebase/config.js";
import {
  ref,
  uploadBytes,
  getDownloadURL
} from "firebase/storage";
import imageCompression from "browser-image-compression";

export async function getAllTrainers(userId) {
  try {
    const trainersRef = collection(db, "users_trainer");
    let usersMap = new Map();

    // Query live trainers
    const liveQuery = query(trainersRef, where("isLive", "==", true));
    const liveSnapshot = await getDocs(liveQuery);

    liveSnapshot.forEach((doc) => {
      usersMap.set(doc.id, { id: doc.id, ...doc.data() });
    });

    // If userId exists, fetch that trainer separately
    if (userId) {
      const trainerDoc = await getDoc(doc(trainersRef, userId));

      if (trainerDoc.exists()) {
        usersMap.set(trainerDoc.id, {
          id: trainerDoc.id,
          ...trainerDoc.data(),
        });
      }
    }

    return { success: true, users: Array.from(usersMap.values()) };

  } catch (error) {
    console.error("Error getting request:", error);
    return { success: false, error: error.message };
  }
}

export async function getTrainerById(userId) {
  try {
    const userRef = doc(db, "users_trainer", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      console.log("No such document!");
      return { success: true, userData: {} };
    }

    // Fetch subcollections in parallel
    // const postsRef = collection(userRef, "posts");
    // const reviewsRef = collection(userRef, "reviews");

    // const [postsSnap, reviewsSnap] = await Promise.all([
    //   getDocs(postsRef),
    //   getDocs(reviewsRef),
    // ]);

    // const posts = postsSnap.docs.map(doc => ({
    //   id: doc.id,
    //   ...doc.data(),
    // }));

    // const reviews = reviewsSnap.docs.map(doc => ({
    //   id: doc.id,
    //   ...doc.data(),
    // }));

    return {
      success: true,
      userData: {
        id: userSnap.id,
        ...userSnap.data(),
      },
      // posts,
      reviews:[],
    };

  } catch (error) {
    console.error("Error getting user:", error);
    return { success: false, error: error.message };
  }
}

export async function updateTrainer(data, userId) {
  try {
    const userDocRef = doc(db, "users_trainer", userId);
    await setDoc(userDocRef, {
      ...data
    }, { merge: true });
    return { success: true };
  } catch (error) {
    console.error('Error updating trainer:', error);
    return { success: false, error: error.message };
  }
}


export async function getCourses(userId) {
  try {
    if (!userId) throw new Error('User ID is required');

    const coursesRef = collection(db, 'users_trainer', userId, 'courses');
    const q = query(coursesRef, orderBy('createdAt', 'desc'));

    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error(error, 'getCourses');
  }
}


export async function createCourse(userId, courseData) {
  try {
    if (!userId) throw new Error('User ID is required');

    const coursesRef = collection(db, 'users_trainer', userId, 'courses');

    const docRef = await addDoc(coursesRef, {
      ...courseData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return docRef.id;
  } catch (error) {
    console.error(error, 'createCourse');
  }
}


export async function getCourseById(userId, courseId) {
  try {
    if (!userId || !courseId) {
      throw new Error('User ID and Course ID are required');
    }

    const courseRef = doc(
      db,
      'users_trainer',
      userId,
      'courses',
      courseId
    );

    const snap = await getDoc(courseRef);

    if (!snap.exists()) {
      throw { code: 'not-found' };
    }

    return {
      id: snap.id,
      ...snap.data(),
    };
  } catch (error) {
    console.error(error, 'getCourseById');
  }
}

export async function updateCourse(userId, courseId, updates) {
  try {
    if (!userId || !courseId) {
      throw new Error('User ID and Course ID are required');
    }

    const courseRef = doc(
      db,
      'users_trainer',
      userId,
      'courses',
      courseId
    );

    await updateDoc(courseRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error(error, 'updateCourse');
  }
}

export async function deleteCourse(userId, courseId) {
  try {
    if (!userId || !courseId) {
      throw new Error('User ID and Course ID are required');
    }

    const courseRef = doc(
      db,
      'users_trainer',
      userId,
      'courses',
      courseId
    );

    await deleteDoc(courseRef);
  } catch (error) {
    console.error(error, 'deleteCourse');
  }
}


/**
 * Upload trainer profile pictures with static filenames
 * @param {Array<{file: File, staticName: string}>} fileItems - Array of objects with file and static name
 * @param {string} userId - Trainer user ID
 * @returns {Promise<Array<{name: string, url: string}>>}
 */
export async function uploadTrainerPictures(fileItems, userId) {
  if (!fileItems?.length) return [];

  const compressionOptions = {
    maxSizeMB: 0.8,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    fileType: "image/jpeg"
  };

  const uploadPromises = fileItems.map(async ({ file, staticName }) => {
    // 1️⃣ Compress image
    const compressedFile = await imageCompression(file, compressionOptions);

    // 2️⃣ Use static filename to ensure overwrites
    const fileName = `${staticName}.jpg`;

    // 3️⃣ Upload to Firebase Storage (static path = automatic overwrite)
    const storageRef = ref(
      storage,
      `trainerPictures/${userId}/${fileName}`
    );

    await uploadBytes(storageRef, compressedFile, {
      contentType: "image/jpeg"
    });

    // 4️⃣ Get public download URL (add cache buster for immediate refresh)
    const url = await getDownloadURL(storageRef);

    return {
      name: staticName,
      url: `${url}&t=${Date.now()}`
    };
  });

  return Promise.all(uploadPromises);
}


/**
 * Upload course lesson images to Firebase Storage
 * @param {Array<{file: File, imageId: string}>} fileItems - Array of objects with file and unique image ID
 * @param {string} userId - Trainer user ID
 * @param {string} courseId - Course ID
 * @returns {Promise<Array<{imageId: string, url: string}>>}
 */
export async function uploadCoursePictures(fileItems, userId, courseId) {
  if (!fileItems?.length) return [];

  const compressionOptions = {
    maxSizeMB: 0.8,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    fileType: "image/jpeg"
  };

  const uploadPromises = fileItems.map(async ({ file, imageId }) => {
    // Compress image
    const compressedFile = await imageCompression(file, compressionOptions);

    // Upload to Firebase Storage at courseAssets/{userId}/courses/{courseId}/{imageId}
    const storageRef = ref(
      storage,
      `courseAssets/${userId}/courses/${courseId}/${imageId}.jpg`
    );

    await uploadBytes(storageRef, compressedFile, {
      contentType: "image/jpeg"
    });

    // Get public download URL with cache buster
    const url = await getDownloadURL(storageRef);

    return {
      imageId,
      url: `${url}&t=${Date.now()}`
    };
  });

  return Promise.all(uploadPromises);
}


/**
 * Delete a course image from Firebase Storage
 * @param {string} userId - Trainer user ID
 * @param {string} courseId - Course ID
 * @param {string} imageId - Image ID to delete
 */
export async function deleteCourseImage(userId, courseId, imageId) {
  try {
    const { deleteObject } = await import("firebase/storage");
    const storageRef = ref(
      storage,
      `courseAssets/${userId}/courses/${courseId}/${imageId}.jpg`
    );
    await deleteObject(storageRef);
  } catch (error) {
    // Silently fail if file doesn't exist
    if (error.code !== 'storage/object-not-found') {
      console.error('Error deleting course image:', error);
    }
  }
}
